import { useCallback, useMemo } from 'react';

import { EyeIcon, ShieldOffIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { MODAL_DIALOG_PREVIEW } from '@/components/organisms/Modals/ModalConductor';
import { renderDialogIcon } from '@/components/organisms/SubNav/TreeView/Item';
import AuthPointsSelector from '@/components/organisms/Toolkit/ToolkitAuth/shared/AuthPointsSelector';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { AutocompleteOption, Autocomplete } from '@/components/ui/custom/Autocomplete/Autocomplete';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import useDialogs from '@/hooks/useDialogs';
import { pushModal } from '@/redux/modals/actions';
import { selectBrainId } from '@/redux/session/selectors';
import { AuthConfig, AuthStep, AuthStepFailure } from '@/shared/types/node';
import { formatSecondsToReadable } from '@/util/dateUtils';

import { AuthPageStepPanelProps } from '../types';
import useAuthPageValidation from '../useAuthPageValidation';
import AuthPagePanelWrapper from './AuthPagePanelWrapper';

interface NodeOption {
  dialog_name: string;
  dialog_id: string;
  value: string;
  label: string;
  node_type: string;
}

// Mirrors the brain reminder dropdown values, capped at the backend's 60-minute
// max (orchestrator: max_step_minutes Joi.min(1).max(60)).
const TIMEOUT_OPTIONS_MINUTES = [1, 3, 5, 10, 15, 30, 60];
const TIMEOUT_DEFAULT_MINUTES = 5;
const MAX_ATTEMPTS_DEFAULT = 3;

const AuthPageFailurePanel = ({
  step,
  authConfig,
  onUpdate,
  onClose,
  showNodeSelector = false,
  showErrors = false,
}: AuthPageStepPanelProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const brainId = useSelector(selectBrainId);
  const { detailedOptions } = useDialogs(brainId);

  const stepFieldKey = `${step}_step` as const;
  const stepConfig: AuthStep | undefined = authConfig[stepFieldKey];

  const { getFieldError } = useAuthPageValidation(authConfig, showErrors);
  const guidelineError = getFieldError(`${step}-failure-guideline`);
  const failureNodeError = getFieldError('failure_node_id');

  const failure = stepConfig?.failure;
  const failureNodeId = authConfig.failure_node_id ?? '';

  const nodeOptions: AutocompleteOption<NodeOption>[] = useMemo(
    () =>
      detailedOptions
        // Unknown nodes are catch-all entry points and never make sense as a
        // post-auth jump target — keep them out of the picker.
        .filter((opt) => opt.node_type !== 'unknown')
        .map((opt) => ({
          value: opt.value,
          label: opt.label,
          category: opt.dialog_name,
          data: opt as NodeOption,
        })),
    [detailedOptions]
  );

  const selectedNode = useMemo(
    () => detailedOptions.find((opt) => opt.value === failureNodeId),
    [detailedOptions, failureNodeId]
  );

  const updateFailure = useCallback(
    (updates: Partial<AuthStepFailure>) => {
      onUpdate({
        [stepFieldKey]: { ...stepConfig, failure: { ...failure, ...updates } },
      } as Partial<AuthConfig>);
    },
    [onUpdate, stepConfig, stepFieldKey, failure]
  );

  const handleNodeChange = useCallback(
    (value: string) => {
      onUpdate({ failure_node_id: value || undefined });
    },
    [onUpdate]
  );

  const handlePreviewDialog = useCallback(() => {
    if (!selectedNode) {
      return;
    }
    dispatch(
      pushModal(MODAL_DIALOG_PREVIEW, {
        name: selectedNode.dialog_name,
        dialogId: selectedNode.dialog_id,
      })
    );
  }, [dispatch, selectedNode]);

  const hasMaxAttempts = failure?.max_attempts != null;

  const hasGuideline = failure?.guideline != null;
  const hasTimeout = failure?.max_step_minutes != null;

  return (
    <AuthPagePanelWrapper
      title={t(
        step === 'identification'
          ? 'dialog.auth.failure_label_identification'
          : 'dialog.auth.failure_label_verification'
      )}
      icon={ShieldOffIcon}
      onClose={onClose}
    >
      <div className="space-y-5">
        {/* Incorrect responses */}
        <div className="space-y-3">
          <div className="flex items-start gap-2">
            <Checkbox
              id="failure-max-attempts"
              checked={hasMaxAttempts}
              onCheckedChange={(checked) => {
                // Disabled is an explicit `null`, uniform across all three exit conditions
                // below: JSON.stringify drops `undefined`, so an unchecked field would be
                // omitted from the PUT and its stored value never cleared. `null` survives
                // serialization and clears it.
                updateFailure({ max_attempts: checked ? MAX_ATTEMPTS_DEFAULT : null });
              }}
              className="mt-0.5"
            />
            <div className="space-y-0.5">
              <Label htmlFor="failure-max-attempts" className="text-sm font-semibold">
                {t('dialog.auth.failure_max_attempts_label')}
              </Label>
              <p className="text-xs text-foreground-muted">
                {t('dialog.auth.failure_max_attempts_description')}
              </p>
            </div>
          </div>
          {hasMaxAttempts && (
            <div className="ml-6 space-y-2">
              <Label className="text-xs text-foreground-muted">
                {t('dialog.auth.failure_max_attempts_sublabel')}
              </Label>
              <AuthPointsSelector
                value={failure.max_attempts!}
                onChange={(val) => updateFailure({ max_attempts: val })}
              />
            </div>
          )}
        </div>

        {/* Guideline-based */}
        <div className="space-y-3">
          <div className="flex items-start gap-2">
            <Checkbox
              id="failure-guideline"
              checked={hasGuideline}
              onCheckedChange={(checked) => {
                // `''` (not `null`) when enabled: `'' != null` keeps the box checked so the
                // textarea stays open for the check-then-type flow.
                updateFailure({ guideline: checked ? '' : null });
              }}
              className="mt-0.5"
            />
            <div className="space-y-0.5">
              <Label htmlFor="failure-guideline" className="text-sm font-semibold">
                {t('dialog.auth.failure_guideline_label')}
              </Label>
              <p className="text-xs text-foreground-muted">
                {t('dialog.auth.failure_guideline_description')}
              </p>
            </div>
          </div>
          {hasGuideline && (
            <div className="ml-6 space-y-1.5">
              <Textarea
                value={failure.guideline ?? ''}
                onChange={(e) => updateFailure({ guideline: e.target.value })}
                placeholder={t('dialog.auth.failure_guideline_placeholder')}
                rows={3}
                aria-invalid={!!guidelineError}
                className="aria-invalid:border-warning"
              />
              {guidelineError && <p className="text-xs text-warning">{guidelineError}</p>}
            </div>
          )}
        </div>

        {/* Authentication timeout */}
        <div className="space-y-3">
          <div className="flex items-start gap-2">
            <Checkbox
              id="failure-timeout"
              checked={hasTimeout}
              onCheckedChange={(checked) => {
                // The orchestrator no longer re-defaults an absent timeout to 5, so `null` persists "off".
                updateFailure({ max_step_minutes: checked ? TIMEOUT_DEFAULT_MINUTES : null });
              }}
              className="mt-0.5"
            />
            <div className="space-y-0.5">
              <Label htmlFor="failure-timeout" className="text-sm font-semibold">
                {t('dialog.auth.failure_timeout_checkbox_label')}
              </Label>
              <p className="text-xs text-foreground-muted">
                {t('dialog.auth.failure_timeout_description')}
              </p>
            </div>
          </div>
          {hasTimeout && (
            <div className="ml-6 space-y-2">
              <Label className="text-xs text-foreground-muted">
                {t('dialog.auth.failure_timeout_label')}
              </Label>
              <Select
                value={String(failure.max_step_minutes ?? TIMEOUT_DEFAULT_MINUTES)}
                onValueChange={(val) => updateFailure({ max_step_minutes: parseInt(val, 10) })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('dialog.auth.failure_timeout_placeholder')} />
                </SelectTrigger>
                <SelectContent>
                  {TIMEOUT_OPTIONS_MINUTES.map((mins) => (
                    <SelectItem key={mins} value={String(mins)}>
                      {formatSecondsToReadable(mins * 60)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Trigger node on failure */}
        {showNodeSelector && (
          <div className="space-y-2">
            <Label className="text-sm font-semibold">{t('dialog.auth.failure_node_label')}</Label>
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <Autocomplete<NodeOption>
                  options={nodeOptions}
                  value={failureNodeId}
                  onChange={handleNodeChange}
                  placeholder={t('dialog.jump_to.placeholder')}
                  className="py-1.5"
                  renderOption={(option) => (
                    <span className="flex items-center gap-2">
                      {option.data?.node_type && renderDialogIcon(option.data.node_type)}
                      <span className="truncate">{option.label}</span>
                    </span>
                  )}
                  renderValue={(option) => (
                    <span className="flex items-center gap-2">
                      {option.data?.node_type && renderDialogIcon(option.data.node_type)}
                      <span className="truncate">{option.label}</span>
                    </span>
                  )}
                />
              </div>
              {selectedNode?.dialog_id && (
                <InfoTooltip content={t('ai_agents.reminder.preview_tooltip')} side="top">
                  <Button variant="outline-neutral" size="icon-md" onClick={handlePreviewDialog}>
                    <EyeIcon size={16} />
                  </Button>
                </InfoTooltip>
              )}
            </div>
            {failureNodeError && <p className="text-xs text-warning">{failureNodeError}</p>}
            <p className="text-xs text-foreground-muted">
              {t('dialog.auth.failure_node_description')}
            </p>
          </div>
        )}
      </div>
    </AuthPagePanelWrapper>
  );
};

export default AuthPageFailurePanel;
